// TrustAccessPage.js — client-facing grant-access screen (institution M4)
// Lets a trust owner grant "WingPoint Trust Group" (their fiduciary org) scoped,
// revocable access to their trust. Calls POST /api/trusts/{trust_id}/org-grants.
// Rendered only when the org feature is on; gracefully empty otherwise.
import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { fetchWithAuth } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import { showError, humanizeErrorCode } from '@/utils/errors';
import PageHeader from '@/components/PageHeader';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, KeyRound, Trash2, RefreshCw, Calendar } from 'lucide-react';

const LEVELS = [
  { id: 'viewer', label: 'View only', desc: 'Your fiduciary can see trust records and minutes, but cannot change anything.' },
  { id: 'preparer', label: 'Prepare minutes', desc: 'Your fiduciary can draft minutes and manage records. You approve before anything is final.' },
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

export default function TrustAccessPage() {
  const { selectedTrust, trusts, subscription, user } = useAuth();
  const { showUpgradeModal } = useUpgradeModal();
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [grants, setGrants] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [orgIdInput, setOrgIdInput] = useState('');
  const [memberIdInput, setMemberIdInput] = useState('');
  const [members, setMembers] = useState([]);
  const [level, setLevel] = useState('viewer');
  const [months, setMonths] = useState('12');
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const trustId = selectedTrust?.trust_id;
  const isOwner = Boolean(selectedTrust);

  // Plan gate (2026-10-14 Jeff re-tier decision): Client Access grants are an
  // Estate/Advisor capability — not Trustee, not free. Server enforces the
  // same rule with 402; this renders a clean upgrade screen instead of a
  // dead-end page. Admins and granted-side org members always pass.
  const normalizeTier = (planType) => (planType === 'monthly' || planType === 'annual' ? 'trustee' : planType);
  const planTier = normalizeTier(subscription?.plan_type);
  const planAllowsClientAccess = ['estate', 'advisor', 'wingpoint'].includes(planTier);
  const planGateResolved = !subscription || ['estate', 'advisor', 'wingpoint', 'trustee', 'free', 'forever_free', 'trial', 'none'].includes(planTier);
  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === 'contact@trustoffice.app';
  const showPlanUpgrade = !subscription?.is_read_only && planGateResolved && !isAdmin && !planAllowsClientAccess;

  const load = useCallback(async () => {
    if (!trustId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [orgsRes, grantsRes] = await Promise.all([
        fetchWithAuth('/orgs'),
        fetchWithAuth(`/trusts/${trustId}/org-grants`),
      ]);
      if (orgsRes.ok) setOrgs(await orgsRes.json());
      if (grantsRes.ok) setGrants(await grantsRes.json());
    } catch (e) {
      showError(toast, e, { operation: 'load_access', page: 'TrustAccess' });
    } finally {
      setLoading(false);
    }
  }, [trustId]);

  useEffect(() => { load(); }, [load]);

  const activeGrants = grants.filter(g => g.status === 'active');
  const pastGrants = grants.filter(g => g.status !== 'active');

  // Load the org's member roster whenever the selected org changes, so the
  // grant dialog can ask WHO receives access (never silently the owner).
  useEffect(() => {
    setMemberIdInput('');
    setMembers([]);
    if (!orgIdInput.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/orgs/${encodeURIComponent(orgIdInput.trim())}/members`);
        if (!res.ok || cancelled) return;
        const list = await res.json();
        if (!cancelled && Array.isArray(list)) setMembers(list.filter(Boolean));
      } catch { /* roster is a convenience — grant POST re-validates member_id */ }
    })();
    return () => { cancelled = true; };
  }, [orgIdInput, grantOpen]);

  const submitGrant = async () => {
    if (!memberIdInput.trim()) {
      toast.error('Choose which team member receives this access.');
      return;
    }
    if (!attested) { toast.error('Please confirm the attestation before granting.'); return; }
    setSubmitting(true);
    try {
      const expires = new Date();
      expires.setMonth(expires.getMonth() + Number(months || 12));
      const res = await fetchWithAuth(`/trusts/${trustId}/org-grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_id: orgIdInput.trim(),
          member_id: memberIdInput.trim(),
          level,
          expires_at: expires.toISOString(),
          attested_delegation: true,
          attestation_ref: `ui-${Date.now()}`,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.detail?.code;
        if (code === 'expiry_exceeds_365_days') toast.error('Grant window cannot exceed 12 months.');
        else if (res.status === 404) toast.error('Feature not available or org not found — check the org ID.');
        else if (res.status === 403) toast.error('Only the trust owner can grant access.');
        else toast.error(typeof body?.detail === 'object'
          ? (body?.detail?.message || humanizeErrorCode(body?.detail?.code) || 'Grant failed.')
          : humanizeErrorCode(body?.detail) || 'Grant failed.');
        return;
      }
      toast.success('Access granted. A notice email has been sent.');
      setGrantOpen(false); setAttested(false); setOrgIdInput(''); setMemberIdInput(''); setLevel('viewer');
      await load();
    } catch (e) {
      showError(toast, e, { operation: 'grant_access', page: 'TrustAccess' });
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (grantId) => {
    setRevoking(grantId);
    try {
      const res = await fetchWithAuth(`/trusts/${trustId}/org-grants/${grantId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Access revoked.'); await load(); }
      else toast.error('Revoke failed — try again or contact support.');
    } catch (e) {
      showError(toast, e, { operation: 'revoke_access', page: 'TrustAccess' });
    } finally {
      setRevoking(null);
    }
  };

  if (showPlanUpgrade) {
    return (
      <div className="main-layout" data-testid="trust-access-page">
        <Sidebar />
        <main className="main-content dot-grid">
          <div className="page-container">
            <PageHeader
              title="Trust Access"
              subtitle="Part of the Estate and Advisor plans."
            />
            <div className="card-trust mt-4 py-10 text-center" data-testid="trust-access-plan-upgrade">
              <KeyRound className="w-10 h-10 text-navy/30 mx-auto mb-3" />
              <h2 className="font-serif text-xl text-navy mb-2">Client Access is an Estate &amp; Advisor feature</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Grant your fiduciary organization time-boxed, revocable access to this trust —
                with an audit trail and email notices. Available on the Estate and Advisor plans.
              </p>
              <Button
                className="btn-primary inline-block"
                data-testid="trust-access-upgrade-btn"
                onClick={() => showUpgradeModal('use Client Access', 'plan_gate', 'trust_access_page')}
              >
                View upgrade options
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!trustId) {
    return (
      <div className="main-layout" data-testid="trust-access-page">
        <Sidebar />
        <main className="main-content dot-grid">
          <div className="page-container">
            <h1 className="page-title">Trust Access</h1>
            {/* P0-2 (2026-10-01 QA): a dead end when the only relevant trust is
                a pre-staged client trust — those are opened from the Org
                Console, and org access is granted from inside each trust. */}
            {trusts.length === 0 ? (
              <div className="card-trust mt-4 py-10 text-center" data-testid="trust-access-empty">
                <KeyRound className="w-10 h-10 text-navy/30 mx-auto mb-3" />
                <h2 className="font-serif text-xl text-navy mb-2">No trusts to manage yet</h2>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Client trusts your organization works on open from the Org Console. Granting
                  a team member access happens inside each trust, when a client shares it with you.
                </p>
                <Link to="/org-console" className="btn-primary inline-block">
                  Open Org Console
                </Link>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Select a trust to manage who has access to it.</p>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="main-layout" data-testid="trust-access-page">
      <Sidebar />
      <main className="main-content dot-grid">
        <div className="page-container">
          <PageHeader
            title="Trust Access"
            subtitle={`Control which fiduciaries can act on ${selectedTrust?.name || selectedTrust?.trust_name || 'this trust'} — and take access back any time.`}
            help={{
              items: [
                { text: 'Grant your fiduciary organization scoped, time-boxed access to this trust' },
                { text: 'Pick the access level — view-only or prepare-minutes — and choose who receives it' },
                { text: 'Every grant is logged, notices you by email, and can be revoked instantly' },
              ],
              taPrompt: 'Help me understand Trust Access and client portals',
            }}
            actions={
              <div className="flex gap-3">
                <Button variant="outline" className="btn-secondary" onClick={load} data-testid="refresh-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Button className="btn-primary" onClick={() => setGrantOpen(true)} data-testid="grant-access-btn">
                  <KeyRound className="w-4 h-4 mr-2" /> Grant Access
                </Button>
              </div>
            }
          />

          <Card className="card-trust mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-navy mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  Granting access gives a fiduciary organization a scoped key to this trust — nothing more.
                  Every grant is logged, notices you by email, expires within 12 months, and can be revoked by you instantly.
                </div>
              </div>
            </CardContent>
          </Card>

          <h2 className="font-serif text-lg text-navy mb-3">Active access</h2>
          {loading ? (
            <div className="card-trust skeleton h-24 w-full mb-4" />
          ) : activeGrants.length === 0 ? (
            <div className="card-trust text-center py-10 mb-6">
              <KeyRound className="w-10 h-10 text-navy/30 mx-auto mb-3" />
              <h3 className="font-serif text-md text-navy mb-1">No outside access right now</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Only you can work on this trust. To let your fiduciary help with minutes and records, grant them access above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {activeGrants.map(g => (
                <Card className="card-trust" key={g.grant_id} data-testid={`grant-card-${g.grant_id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="capitalize">{g.level === 'preparer' ? 'Prepare minutes' : 'View only'}</Badge>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => revoke(g.grant_id)} disabled={revoking === g.grant_id} data-testid={`revoke-${g.grant_id}`}>
                        <Trash2 className="w-4 h-4 mr-1" /> {revoking === g.grant_id ? 'Revoking…' : 'Revoke'}
                      </Button>
                    </div>
                    <div className="text-sm text-navy">Org: <code className="text-xs">{g.org_id}</code></div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar className="w-3.5 h-3.5" /> Expires {fmtDate(g.expires_at)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {pastGrants.length > 0 && (
            <>
              <h2 className="font-serif text-lg text-navy mb-3">Past access</h2>
              <div className="card-trust mb-6">
                {pastGrants.map(g => (
                  <div key={g.grant_id} className="flex items-center justify-between py-2 px-4 border-b last:border-0 border-border/50 text-sm">
                    <span className="text-muted-foreground"><code className="text-xs">{g.org_id}</code> · {g.level}</span>
                    <span className="text-xs text-muted-foreground capitalize">{g.status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent data-testid="grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant access to your fiduciary</DialogTitle>
            <DialogDescription>
              Granting is logged and a notice email is sent. You can revoke at any time from this page.
            </DialogDescription>
          </DialogHeader>
          {orgs.length > 0 && (
            <div className="mb-3">
              <Label className="text-sm text-navy">Organizations you belong to</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {orgs.map(o => (
                  <Badge
                    key={o.org_id}
                    variant={orgIdInput === o.org_id ? 'default' : 'secondary'}
                    className="cursor-pointer"
                    onClick={() => setOrgIdInput(o.org_id)}
                  >
                    {o.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="org-id">Organization ID</Label>
              <Input
                id="org-id"
                placeholder="org_..."
                value={orgIdInput}
                onChange={e => setOrgIdInput(e.target.value)}
                data-testid="org-id-input"
              />
            </div>
            <div>
              <Label htmlFor="grant-member">Which team member gets access</Label>
              <select
                id="grant-member"
                className="input-trust w-full mt-2 p-2 text-sm"
                value={memberIdInput}
                onChange={e => setMemberIdInput(e.target.value)}
                data-testid="member-select"
              >
                <option value="">{orgIdInput.trim() ? (members.length ? 'Choose a team member…' : 'Loading team members…') : 'Choose an organization first…'}</option>
                {members.map(m => (
                  <option key={m.member_id} value={m.member_id}>
                    {(m.name || m.email)} · {m.status === 'active' ? 'Active' : m.status === 'suspended' ? 'Suspended' : 'Invited'}
                    {m.role === 'owner' ? ' · Owner' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Access is given to the person you pick here — it doesn't go to everyone.
              </p>
            </div>
            <div>
              <Label className="text-sm text-navy">Access level</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {LEVELS.map(l => (
                  <label key={l.id} className={`flex items-start gap-2 p-3 border cursor-pointer ${level === l.id ? 'border-primary bg-accent/40' : 'border-border'}`}>
                    <input type="radio" name="level" value={l.id} checked={level === l.id} onChange={() => setLevel(l.id)} className="mt-1" data-testid={`level-${l.id}`} />
                    <span>
                      <span className="text-sm font-medium text-navy">{l.label}</span>
                      <span className="block text-xs text-muted-foreground">{l.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm text-navy">Expires after</Label>
              <select
                className="input-trust w-full mt-2 p-2 text-sm"
                value={months} onChange={e => setMonths(e.target.value)} data-testid="expiry-select"
              >
                <option value="1">1 month</option>
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months (maximum)</option>
              </select>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={attested} onChange={e => setAttested(e.target.checked)} data-testid="attest-checkbox" />
              <span className="text-muted-foreground">
                I confirm I am the trustee of this trust and I am delegating this access knowingly.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={submitGrant} disabled={submitting || !attested || !orgIdInput.trim() || !memberIdInput.trim()} data-testid="confirm-grant-btn">
              {submitting ? 'Granting…' : 'Grant Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}